"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { ChevronRight, ArrowLeft, ArrowRight } from "lucide-react";
import {
  buildDocsHref,
  DOCS_PRODUCT_QUERY_PARAM,
  getAllDocsNavigation,
  getDocsNavigation,
  getFlattenedNav,
  resolveDocsProduct,
} from "@/config/navigation";

export function Breadcrumbs() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeProduct = resolveDocsProduct(searchParams.get(DOCS_PRODUCT_QUERY_PARAM), pathname || undefined);
  
  const flatNav = getFlattenedNav(getDocsNavigation(activeProduct));
  const currentItem = flatNav.find((item) => item.href === pathname)
    || getFlattenedNav(getAllDocsNavigation()).find((item) => item.href === pathname);
  
  if (!currentItem) return null;

  return (
    <nav className="mb-4 flex flex-wrap items-center gap-2 text-sm text-[#7c879b] dark:text-[#94a3b8]">
      <Link 
        href="/" 
        className="rounded-full border border-white/80 dark:border-white/10 bg-white/70 dark:bg-white/5 px-3 py-1.5 hover:text-[#0f172a] dark:hover:text-[#f8fafc] dark:hover:bg-white/10 transition-all duration-200"
      >
        首页
      </Link>
      <ChevronRight className="h-3.5 w-3.5 text-[#c1c9da] dark:text-[#475569]" />
      <span className="rounded-full border border-[#dfe7ff] dark:border-[#335AFF]/30 bg-[#f4f7ff] dark:bg-[#335AFF]/10 px-3 py-1.5 font-medium text-[#17325c] dark:text-[#8da4ff]">{currentItem.title}</span>
    </nav>
  );
}

export function PrevNext() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeProduct = resolveDocsProduct(searchParams.get(DOCS_PRODUCT_QUERY_PARAM), pathname || undefined);
  const flatNav = getFlattenedNav(getDocsNavigation(activeProduct));
  const currentIndex = flatNav.findIndex((item) => item.href === pathname);

  if (currentIndex === -1) return null;

  const prev = currentIndex > 0 ? flatNav[currentIndex - 1] : null;
  const next = currentIndex < flatNav.length - 1 ? flatNav[currentIndex + 1] : null;

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {prev ? (
        <Link
          href={buildDocsHref(prev.href, activeProduct, searchParams)}
          className="group surface-panel dark:bg-[#1e293b]/50 dark:border-white/10 relative flex min-h-[88px] flex-col rounded-[1rem] p-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_40px_rgba(51,90,255,0.08)] dark:hover:shadow-[0_22px_40px_rgba(0,0,0,0.4)]"
        >
          <div className="absolute inset-0 rounded-[1rem] bg-gradient-to-br from-[#3759FD]/6 dark:from-[#3759FD]/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          <div className="mb-1.5 flex items-center text-[11px] text-[#8a96ab] dark:text-[#94a3b8] transition-colors group-hover:text-[#335AFF] dark:group-hover:text-[#8da4ff]">
            <ArrowLeft className="mr-1 h-3 w-3" />
            上一页
          </div>
          <div className="mt-auto text-[15px] font-semibold text-[#0f172a] dark:text-[#f8fafc] transition-colors group-hover:text-[#335AFF] dark:group-hover:text-[#8da4ff]">{prev.title}</div>
        </Link>
      ) : <div />}

      {next ? (
        <Link
          href={buildDocsHref(next.href, activeProduct, searchParams)}
          className="group surface-panel dark:bg-[#1e293b]/50 dark:border-white/10 relative flex min-h-[88px] flex-col items-end rounded-[1rem] p-4 text-right transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_22px_40px_rgba(51,90,255,0.08)] dark:hover:shadow-[0_22px_40px_rgba(0,0,0,0.4)]"
        >
          <div className="absolute inset-0 rounded-[1rem] bg-gradient-to-bl from-[#3759FD]/6 dark:from-[#3759FD]/10 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          <div className="mb-1.5 flex items-center text-[11px] text-[#8a96ab] dark:text-[#94a3b8] transition-colors group-hover:text-[#335AFF] dark:group-hover:text-[#8da4ff]">
            下一页
            <ArrowRight className="ml-1 h-3 w-3" />
          </div>
          <div className="mt-auto text-[15px] font-semibold text-[#0f172a] dark:text-[#f8fafc] transition-colors group-hover:text-[#335AFF] dark:group-hover:text-[#8da4ff]">{next.title}</div>
        </Link>
      ) : <div />}
    </div>
  );
}
